import { Store } from '@ngxs/store'
import { Injectable } from '@angular/core'
import { QuoteMetaState } from 'src/store/quote/quote.meta.state'
import {
  IWarning,
  ProductTypes,
  WarningActions,
  WarningReferences,
} from '../smart-suggestion.model'
import { SmartSuggestionMetaState } from '../smart-suggestion.meta.state'
import { Utils } from 'src/helpers/utils.helper'
import { RemoveWarningAction } from '../smart-suggestion.meta.actions'

@Injectable({ providedIn: 'root' })
export class PanelsHelper {
  constructor(
    private store: Store,
    private utils: Utils,
  ) {}

  findPanelWarningReasons(): IWarning[] {
    const warnings = this.store.selectSnapshot(
      SmartSuggestionMetaState.getWarnings,
    )
    const panelWarnings = warnings.filter(
      (warning) =>
        warning.reference === WarningReferences.PANEL_X_INVERTER_NOT_MATCHED,
    )
    return panelWarnings
  }

  validatePanelsConstraints() {
    const compositionSuggestions = this.store.selectSnapshot(
      SmartSuggestionMetaState.getCompositionSuggestions,
    )
    const selectedPanel = this.store.selectSnapshot(
      QuoteMetaState.getSelectedPanel,
    )
    const selectedInverters = this.store.selectSnapshot(
      QuoteMetaState.getSelectedInverters,
    )
    const foundWarnings = this.findPanelWarningReasons()
    if (
      selectedInverters.length === 0 ||
      !selectedPanel ||
      !compositionSuggestions
    ) {
      if (foundWarnings.length > 0) {
        this.store.dispatch(new RemoveWarningAction(foundWarnings[0].reference))
      }
      return []
    }
    const { panels } = compositionSuggestions
    const quantities = panels.reduce(
      (acc, item) => {
        const quantity = item.content[0].quantity
        acc.min = Math.min(acc.min, quantity)
        acc.max = Math.max(acc.max, quantity)
        return acc
      },
      { min: Infinity, max: -Infinity },
    )
    const isLessThanMin = selectedPanel.quantity < quantities.min
    const isGreaterThanMax = selectedPanel.quantity > quantities.max
    if (!isLessThanMin && !isGreaterThanMax) {
      if (foundWarnings.length > 0) {
        this.store.dispatch(new RemoveWarningAction(foundWarnings[0].reference))
      }
      return []
    }
    const greaterThanSupportedContent = `${this.utils.getTranslatedKey(
      'You have selected',
    )} ${selectedPanel.quantity}x ${
      selectedPanel.title
    }, ${this.utils.getTranslatedKey(
      'this is more modules than supported by the amount of inverters selected',
    )}`
    const lessThanSupportedContent = `${this.utils.getTranslatedKey(
      'You have selected',
    )} ${selectedPanel.quantity}x ${
      selectedPanel.title
    } ${this.utils.getTranslatedKey(
      'but this amount is less than supported by selected inverters',
    )}`
    const reason = {
      content: isLessThanMin
        ? lessThanSupportedContent
        : greaterThanSupportedContent,
      isRestrictive: true,
      lockedTabs: [7],
      options: [
        {
          actions: [WarningActions.SEE_SUGGESTIONS],
          title: 'See Suggestions',
        },
      ],
      reference: WarningReferences.PANEL_X_INVERTER_NOT_MATCHED,
    }
    return [reason]
  }

  getPanelsSuggestions() {
    const selectedPanel = this.store.selectSnapshot(
      QuoteMetaState.getSelectedPanel,
    )
    const inverters = this.store.selectSnapshot(QuoteMetaState.getInverters)
    const selectedInverters = inverters?.filter((item) => item?.quantity > 0)
    if (!selectedPanel || !inverters || inverters.length === 0) {
      return []
    }
    const { totalMinimumAmount, totalMaximumAmount } =
      this.getInvertersAmountValues(selectedInverters, selectedPanel)
    if (totalMinimumAmount === totalMaximumAmount) {
      return []
    }
    const suggestions = this.getPanelsOptions(
      selectedPanel,
      totalMinimumAmount,
      totalMaximumAmount,
    )
    return suggestions
  }

  private getPanelsOptions(
    selectedPanel: any,
    totalMinimumAmount: number,
    totalMaximumAmount: number,
  ) {
    const selectedPanelQuantity = selectedPanel.quantity
    const options = [
      {
        title: `${totalMinimumAmount}x ${selectedPanel.title}`,
        isSelected: false,
        content: [
          {
            id: selectedPanel.id,
            type: ProductTypes.PANEL,
            quantity: totalMinimumAmount,
          },
        ],
      },
      {
        title: `${selectedPanel.quantity}x ${selectedPanel.title}`,
        isSelected: true,
        content: [
          {
            id: selectedPanel.id,
            type: ProductTypes.PANEL,
            quantity: selectedPanel.quantity,
          },
        ],
      },
      {
        title: `${totalMaximumAmount}x ${selectedPanel.title}`,
        isSelected: false,
        content: [
          {
            id: selectedPanel.id,
            type: ProductTypes.PANEL,
            quantity: totalMaximumAmount,
          },
        ],
      },
    ]
    const betweenMinimumAndMaximum = (
      (totalMinimumAmount + totalMaximumAmount) /
      2
    ).toFixed(0)
    const betweenValuesOption = {
      title: `${betweenMinimumAndMaximum}x ${selectedPanel.title}`,
      isSelected: false,
      content: [
        {
          id: selectedPanel.id,
          type: ProductTypes.PANEL,
          quantity: betweenMinimumAndMaximum,
        },
      ],
    }
    if (selectedPanelQuantity === totalMinimumAmount) {
      options[0].isSelected = true
      options[1] = betweenValuesOption
    }
    if (selectedPanelQuantity === totalMaximumAmount) {
      options[2].isSelected = true
      options[1] = betweenValuesOption
    }
    if (selectedPanelQuantity < totalMinimumAmount) {
      options[1] = betweenValuesOption
    }
    if (selectedPanelQuantity > totalMaximumAmount) {
      options[1] = betweenValuesOption
    }
    return options
  }

  private getInvertersAmountValues(
    selectedInverters: any[],
    selectedPanel: any,
  ) {
    const invertersInPanels = selectedInverters.map((inverter) => {
      return {
        panel: this.getSelectedPanelAtInverter(inverter.id, selectedPanel.id),
        quantity: inverter.quantity,
      }
    })
    const totalMinimumAmount = invertersInPanels.reduce(
      (acc, curr) => acc + curr.panel.minimumAmount * curr.quantity,
      0,
    )
    const totalMaximumAmount = invertersInPanels.reduce(
      (acc, curr) => acc + curr.panel.maximumAmount * curr.quantity,
      0,
    )
    return { totalMinimumAmount, totalMaximumAmount }
  }

  private getSelectedPanelAtInverter(inverterId: string, panelId: string): any {
    const defaultObject = {
      minimumAmount: 0,
      maximumAmount: 0,
    }
    const productsContraints = this.store.selectSnapshot(
      QuoteMetaState.getProductsConstraints,
    )
    const found = productsContraints?.find(
      (item) =>
        item.panelId === panelId.toString() &&
        item.inverterId === inverterId.toString(),
    )
    return found || defaultObject
  }
}
